import { useT } from "../lib/i18n";

const contextFiles = [
  { icon: "📄", name: "proposta-comercial.pdf" },
  { icon: "📊", name: "deck-apresentacao.pdf" },
  { icon: "📝", name: "notas-cliente.txt" },
];

const sourceIcons: Record<string, string> = {
  "proposal.pdf": "📄",
  "proposta.pdf": "📄",
  "propuesta.pdf": "📄",
  transcript: "💬",
  "live transcript": "💬",
  "busca web": "🌐",
  "web search": "🌐",
  "busqueda web": "🌐",
};

function sourceIcon(src: string) {
  const key = src.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return sourceIcons[key] ?? "📎";
}

export function CopilotFeature() {
  const t = useT();
  const m = t.copilot.mockup;

  return (
    <section id="copilot" className="border-t border-margin dark:border-paper/10">
      <div className="mx-auto max-w-6xl px-6 py-24">

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-graphite/30 bg-graphite/5 px-3 py-1 text-xs font-medium text-graphite dark:border-graphite-muted/30 dark:bg-graphite/10 dark:text-graphite-muted">
              <span className="h-1.5 w-1.5 rounded-full bg-graphite dark:bg-graphite-muted" />
              {t.copilot.badge}
            </div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
              {t.copilot.heading}
            </h2>
            <p className="mt-4 text-ink/70 dark:text-paper/70 leading-relaxed">
              {t.copilot.description}
            </p>
          </div>

          <ul className="flex flex-col gap-3 text-sm lg:text-right shrink-0">
            {t.copilot.bullets.map((item) => (
              <li key={item} className="flex items-start lg:justify-end gap-2 text-ink/70 dark:text-paper/70">
                <span className="mt-0.5 h-4 w-4 flex-shrink-0 lg:order-last text-graphite dark:text-graphite-muted">
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3,8 7,12 13,4" />
                  </svg>
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Mockup */}
        <div className="mt-14 rounded-2xl border border-margin dark:border-paper/10 overflow-hidden">

          {/* Top bar */}
          <div className="flex items-center gap-3 px-5 py-3 bg-margin/40 dark:bg-paper/[0.04] border-b border-margin dark:border-paper/10">
            <span className="font-mono text-[11px] text-ink/50 dark:text-paper/50 uppercase tracking-wider">
              {m.barLabel}
            </span>
            <span className="h-px flex-1 bg-margin dark:bg-paper/10" />
            <span className="font-mono text-[11px] text-rec">● REC 14:52</span>
          </div>

          <div className="grid lg:grid-cols-[260px_1fr]">

            {/* Sidebar — context files */}
            <div className="hidden lg:block border-r border-margin dark:border-paper/10 px-5 py-6">
              <p className="font-mono text-[10px] uppercase tracking-widest text-ink/40 dark:text-paper/40 mb-4">
                {m.addFile.replace("Add ", "").replace("Adicionar ", "").replace("Agregar ", "")}
                {" "}
                <span className="text-ink/25 dark:text-paper/25">/ context</span>
              </p>
              <ul className="space-y-2">
                {contextFiles.map((f) => (
                  <li key={f.name} className="flex items-center gap-2.5 rounded-lg px-3 py-2 bg-paper dark:bg-ink/40 border border-margin dark:border-paper/10 text-sm text-ink/70 dark:text-paper/70">
                    <span className="text-base leading-none">{f.icon}</span>
                    <span className="font-mono text-xs truncate">{f.name}</span>
                  </li>
                ))}
                <li className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-ink/15 dark:border-paper/15 text-xs text-ink/40 dark:text-paper/40 cursor-pointer hover:border-ink/30 transition-colors">
                  <span className="text-base leading-none">＋</span>
                  {m.addFile}
                </li>
              </ul>
            </div>

            {/* Main area — transcript + detected question + response */}
            <div className="px-6 py-6 space-y-4">

              {/* Live transcript label */}
              <p className="font-mono text-[10px] uppercase tracking-widest text-ink/40 dark:text-paper/40">
                {m.liveLabel}
              </p>

              {/* Past lines */}
              <div className="space-y-2">
                {m.transcript.map((line) => (
                  <div key={line.time} className="flex items-start gap-3 text-sm text-ink/50 dark:text-paper/40">
                    <span className="font-mono text-[10px] mt-0.5 shrink-0 tabular-nums">{line.time}</span>
                    <span>
                      <span className="font-medium text-ink/60 dark:text-paper/50">{line.speaker}:</span>
                      {" "}{line.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* Detected question */}
              <div className="rounded-xl border-2 border-graphite/25 bg-graphite/[0.04] dark:border-graphite-muted/25 dark:bg-graphite/[0.08] p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm">❓</span>
                  <span className="font-mono text-[10px] uppercase tracking-wider text-graphite dark:text-graphite-muted font-semibold">
                    {m.questionBadge}
                  </span>
                </div>
                <div className="flex items-start gap-3 text-sm text-ink/80 dark:text-paper/80">
                  <span className="font-mono text-[10px] mt-0.5 shrink-0 tabular-nums text-ink/50">{m.detectedQ.time}</span>
                  <span>
                    <span className="font-semibold text-ink dark:text-paper">{m.detectedQ.speaker}:</span>
                    {" "}"{m.detectedQ.text}"
                  </span>
                </div>

                {/* CTA button */}
                <div className="mt-3 flex justify-end">
                  <div className="inline-flex items-center gap-2 rounded-full bg-graphite text-paper px-4 py-1.5 text-xs font-medium cursor-pointer hover:bg-graphite-muted transition-colors select-none">
                    <span>💡</span>
                    {m.ctaButton}
                  </div>
                </div>
              </div>

              {/* Suggested response */}
              <div className="rounded-xl border border-margin dark:border-paper/10 bg-margin/30 dark:bg-paper/[0.03] p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm">✨</span>
                  <span className="font-mono text-[10px] uppercase tracking-wider text-ink/50 dark:text-paper/40 font-semibold">
                    {m.responseBadge}
                  </span>
                </div>
                <p className="text-sm text-ink/80 dark:text-paper/75 leading-relaxed">
                  {m.responseText}
                </p>
                {/* Sources */}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-ink/30 dark:text-paper/30">
                    {m.sourcesLabel}
                  </span>
                  {m.chatSources.map((src) => (
                    <span key={src} className="inline-flex items-center gap-1 rounded-full border border-graphite/20 bg-graphite/5 px-2 py-0.5 font-mono text-[10px] text-graphite dark:text-graphite-muted">
                      <span>{sourceIcon(src)}</span>
                      {src}
                    </span>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
